// «Questa persona può fare questa cosa?» — la stessa domanda che si fa il
// servizio, fatta dalla schermata per decidere se mostrare un pulsante.
//
// La risposta ha due metà, come nel middleware `autorizzaAzione`:
//
//   - il **ruolo** che quella cosa la faceva già prima che i permessi
//     d'azione esistessero (`ancheConRuolo`, dichiarato nel catalogo). Senza
//     questa metà, aggiungere un permesso avrebbe nascosto «Nuovo utente» a
//     tutti i MANAGER: loro il codice non ce l'hanno — e non devono averlo,
//     perché lo fanno di ruolo;
//   - il **permesso** assegnato con un profilo o un'eccezione, per chi quel
//     ruolo non ce l'ha.
//
// La soglia si legge dal catalogo e non è scritta qui: è lo stesso elenco da
// cui la legge il servizio, quindi le due non possono divergere.
//
// Finché i permessi non sono stati letti (o la lettura è fallita) risponde di
// no (fail-closed) per chi non è SUPER_ADMIN: in questo modo nessun pulsante
// revocato con «Nega» o riservato a delega compare durante il caricamento.

import { ROLE_HIERARCHY, permessiDiRuolo } from './rbacRoles.js';

export function puoAzione(codice, { permessi, negati, catalogo, stato, ruolo }) {
  if (!codice) return true;
  if (ruolo === 'SUPER_ADMIN') return true;
  if (stato !== 'pronto') return false;
  if (Array.isArray(negati) && negati.includes(codice)) return false;
  if ((permessi || []).includes(codice)) return true;
  // Quando `negati` è fornito dallo store (`/permessi/me`), `permessi` include
  // già le facoltà di ruolo (`ancheConRuolo`) al netto delle revoche e dei
  // moduli mancanti. Il fallback sulla soglia resta per i chiamanti/test che
  // passano solo `permessi` + `catalogo`.
  if (!Array.isArray(negati)) {
    const voce = (catalogo || []).find((v) => v.codice === codice);
    const soglia = voce && voce.ancheConRuolo;
    if (soglia && (ROLE_HIERARCHY[ruolo] || 0) >= (ROLE_HIERARCHY[soglia] || 99)) return true;
  }
  return false;
}

// «Le è stato assegnato questo codice?» — senza la scorciatoia del ruolo.
//
// Serve dove la schermata era **più stretta** del servizio: in Gestione Utenti
// «Metodi di accesso» e «Importa CSV» si mostravano al solo SUPER_ADMIN anche
// se la rotta ammetteva il MANAGER. Usare `puoAzione` lì avrebbe allargato
// quei due pulsanti a tutti i MANAGER — un cambio di comportamento che
// nessuno ha chiesto. Così invece si aggiunge solo chi il permesso ce l'ha
// davvero.
export function haCodice(codice, { permessi, stato }) {
  if (!codice || stato !== 'pronto') return false;
  return (permessi || []).includes(codice);
}

// Chi può aprire una pagina:
// - `SUPER_ADMIN` entra ovunque;
// - finché lo store permessi è in `iniziale` o `caricamento`, risponde `false`
//   (fail-closed; `ProtectedRoute` mostra `<Loading />` nell'attesa);
// - quando lo store è `pronto`, se la rotta è associata a uno o più codici
//   `vista.*` (`codiciVista` o `codice` da catalogo), decide l'elenco
//   effettivo dei permessi (inclusi deleghe e «Nega»); altrimenti vale
//   `allowedRoles`;
// - solo se `/permessi/me` fallisce (`statoPermessi === 'errore'`), ripiega
//   sui permessi statici di ruolo (`permessiDiRuolo`) o su `allowedRoles`.
export function puoAprirePagina({ codice, codiciVista, permessi, statoPermessi, ruolo, allowedRoles }) {
  if (ruolo === 'SUPER_ADMIN') return true;
  const codici = Array.isArray(codiciVista) && codiciVista.length > 0
    ? codiciVista
    : (codice ? [codice] : []);

  if (statoPermessi === 'pronto') {
    if (codici.length > 0) {
      return codici.some((c) => (permessi || []).includes(c));
    }
    if (!allowedRoles || allowedRoles.length === 0) return true;
    const mio = ROLE_HIERARCHY[ruolo] || 0;
    const minimo = Math.min(...allowedRoles.map((r) => ROLE_HIERARCHY[r] || 99));
    return mio >= minimo;
  }

  if (statoPermessi === 'errore') {
    if (codici.length > 0) {
      const statici = permessiDiRuolo(ruolo);
      return codici.some((c) => statici.includes(c));
    }
    if (!allowedRoles || allowedRoles.length === 0) return true;
    const mio = ROLE_HIERARCHY[ruolo] || 0;
    const minimo = Math.min(...allowedRoles.map((r) => ROLE_HIERARCHY[r] || 99));
    return mio >= minimo;
  }

  return false;
}
